import { METHOD_IDS } from '@/components/admin/reglages/method-contracts';
import { MethodField } from '@/components/admin/reglages/method-field';
import {
  METHOD_STYLE_FIELDS,
  type MethodDraft,
  type MethodStyleField,
  differsFrom,
} from '@/lib/method-draft';
import type { MethodContent, NoyauDefinition } from '@matter_extractor/shared';
import { useId } from 'react';

/**
 * One STYLE of Réglages > Méthode: its name, what recognises it, and its typical
 * words (analyste.md §4.2, maquette `Methode`) - the three fields the second pass
 * reads, one card per noyau of the table of §2.1.
 *
 * The NAME and the TRIPLET come from the table of `shared` and never from the
 * version, which carries neither (§4.2); the COLOUR is the official one, painted
 * as a dot beside the name - the operator sees it, the person never does. It
 * arrives as a value of the table and is applied inline: it is data of the
 * method, not a token of the charte, and no hex is written in this file. The
 * card has no rule across its top, which the cards of Réglages > Noyaux do: the
 * planche draws the dot alone.
 */

export interface MethodStyleCardProps {
  readonly noyau: NoyauDefinition;
  readonly draft: MethodDraft;
  /** The development version the draft started from: what « modifié » is measured against. */
  readonly stored: MethodContent;
  /** A version opened by « Voir », or a vitrine: nothing is typable, and nothing is « modifié ». */
  readonly readOnly: boolean;
  readonly onField: (field: MethodStyleField, text: string) => void;
}

export function MethodStyleCard({ noyau, draft, stored, readOnly, onField }: MethodStyleCardProps) {
  const titleId = useId();
  return (
    <section
      aria-labelledby={titleId}
      data-testid={METHOD_IDS.styleCard}
      data-noyau={noyau.code}
      className="flex min-w-0 flex-col gap-3 rounded-lg border-2 border-gray-200 bg-white p-4"
    >
      <div className="flex min-w-0 items-center gap-1.75">
        <span
          aria-hidden="true"
          data-testid={`${METHOD_IDS.styleCard}-couleur`}
          className="block size-3 shrink-0 rounded-full border border-noir/15"
          style={{ backgroundColor: noyau.color }}
        />
        <h3 id={titleId} className="truncate font-semibold text-gray-800 text-sm leading-4.5">
          {noyau.name}
        </h3>
        <span className="shrink-0 text-gray-600 text-xs leading-4">· {noyau.code}</span>
      </div>

      {METHOD_STYLE_FIELDS.map((field) => {
        const ref = { part: 'styles', key: noyau.code, field } as const;
        return (
          <MethodField
            key={field}
            fieldRef={ref}
            value={draft.styles[noyau.code][field]}
            touched={!readOnly && differsFrom(draft, stored, ref)}
            readOnly={readOnly}
            onChange={(text) => onField(field, text)}
          />
        );
      })}
    </section>
  );
}
